import { Check } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@/lib/cn';

export interface CheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  className?: string | undefined;
  disabled?: boolean | undefined;
  name?: string | undefined;
}

/** Bordered checkbox chip from the mockup filter bar ("Red only", "Missing only"). */
export function CheckboxChip({
  checked,
  onChange,
  label,
  className,
  disabled,
  name,
}: CheckboxProps) {
  return (
    <label
      className={cn(
        'inline-flex h-[34px] cursor-pointer select-none items-center gap-1.5 rounded-lg border border-line bg-surface px-2.5 font-medium text-ink2',
        checked && 'text-ink',
        disabled && 'opacity-50',
        className,
      )}
    >
      <input
        type="checkbox"
        name={name}
        className="sr-only"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span
        aria-hidden
        className={cn(
          'inline-flex size-3.5 items-center justify-center rounded border-2 border-ink3',
          checked && 'border-ink bg-ink text-on-ink',
        )}
      >
        {checked ? <Check className="size-3" strokeWidth={3} /> : null}
      </span>
      {label}
    </label>
  );
}

export interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: ReactNode;
  disabled?: boolean | undefined;
  className?: string | undefined;
  'aria-label'?: string | undefined;
}

/** Toggle switch (green when on, as in the mockup). */
export function Switch({ checked, onChange, label, disabled, className, ...aria }: SwitchProps) {
  return (
    <label
      className={cn(
        'inline-flex cursor-pointer select-none items-center gap-2 text-[13px]',
        disabled && 'opacity-50',
        className,
      )}
    >
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={aria['aria-label']}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative h-5 w-[34px] flex-none rounded-full transition-colors',
          checked ? 'bg-good' : 'bg-line',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 size-4 rounded-full bg-white shadow transition-[left]',
            checked ? 'left-4' : 'left-0.5',
          )}
        />
      </button>
      {label}
    </label>
  );
}

export interface RadioProps {
  checked: boolean;
  onChange: () => void;
  label: ReactNode;
  name: string;
  className?: string | undefined;
}

export function Radio({ checked, onChange, label, name, className }: RadioProps) {
  return (
    <label
      className={cn(
        'inline-flex cursor-pointer select-none items-center gap-1 text-xs font-semibold text-ink2',
        className,
      )}
    >
      <input type="radio" name={name} className="sr-only" checked={checked} onChange={onChange} />
      <span
        aria-hidden
        className={cn(
          'inline-block size-3.5 rounded-full border-2 border-ink3',
          checked && 'border-4 border-orange',
        )}
      />
      {label}
    </label>
  );
}
